import { useCallback, useEffect } from 'react'

import { useGetUserQuery, useUpdateUserActivationDataMutation } from 'common'
import { clearAllSpotlightsFromActivationData } from 'shared/constants/clear_spotlights_activation_data'

import {
  clearBrandRefreshActivationData,
  patchBrandRefreshActivationData,
} from './brandRefreshActivationData'
import {
  clearBrandRefreshDebugOverrides,
  isBrandRefreshDebugActive,
  notifyBrandRefreshDebugUpdated,
  readBrandRefreshDebugOverrides,
  setBrandRefreshDebugCountdownDays,
  setBrandRefreshDebugForceExpired,
  setBrandRefreshDebugStartedDaysAgo,
} from './brandRefreshBetaDebug'
import {
  clearProductRefreshPreviewFromStorage,
  useProductRefreshPreviewContext,
} from '../product_refresh/productRefreshPreviewContext'

/**
 * Headless chrome slot: installs `window.brandRefreshBetaDebug` while the debug
 * trigger is shown. Must stay mounted outside PopoverContent — CUI unmounts
 * panel sections when the popover closes.
 */
export const BrandRefreshBetaDebugConsoleShim = () => {
  const { setOverride: setProductRefreshPreviewOverride } = useProductRefreshPreviewContext()
  const { refetch: refetchUser } = useGetUserQuery(undefined)
  const [updateActivationData] = useUpdateUserActivationDataMutation()

  const persistStartedDaysAgo = useCallback(async (daysAgo: number) => {
    const startedAt = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString()
    await updateActivationData((data) => patchBrandRefreshActivationData(
      data,
      { banner_started_at: startedAt },
    ))
    setBrandRefreshDebugStartedDaysAgo(daysAgo)
  }, [updateActivationData])

  const clearActivationDataAndOverrides = useCallback(async () => {
    clearBrandRefreshDebugOverrides()
    clearProductRefreshPreviewFromStorage()
    setProductRefreshPreviewOverride(false)

    try {
      await updateActivationData(clearBrandRefreshActivationData).unwrap()
      notifyBrandRefreshDebugUpdated()
    } catch {
      await refetchUser()
    }
  }, [refetchUser, setProductRefreshPreviewOverride, updateActivationData])

  const handleClear = useCallback(() => {
    clearActivationDataAndOverrides().catch(() => {})
  }, [clearActivationDataAndOverrides])

  const clearAllSpotlights = useCallback(async () => {
    try {
      await updateActivationData(clearAllSpotlightsFromActivationData).unwrap()
    } catch {
      await refetchUser()
    }
  }, [refetchUser, updateActivationData])

  const handleClearAllSpotlights = useCallback(() => {
    clearAllSpotlights().catch(() => {})
  }, [clearAllSpotlights])

  useEffect(() => {
    if (!isBrandRefreshDebugActive()) return undefined

    window.brandRefreshBetaDebug = {
      expireNow: () => setBrandRefreshDebugForceExpired(true),
      startedDaysAgo: (days: number) => {
        setBrandRefreshDebugStartedDaysAgo(days)
        persistStartedDaysAgo(days).catch(() => {})
      },
      setCountdownDays: setBrandRefreshDebugCountdownDays,
      clearOverrides: () => {
        handleClear()
      },
      clearAllSpotlights: () => {
        handleClearAllSpotlights()
      },
      getState: readBrandRefreshDebugOverrides,
    }

    return () => {
      delete window.brandRefreshBetaDebug
    }
  }, [handleClear, handleClearAllSpotlights, persistStartedDaysAgo])

  return null
}
